import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import MatrixBackground from '../../components/ui/MatrixBackground';
import HeroV2 from '../../components/funnel/HeroV2';
import VSLSection from '../../components/funnel/VSLSection';
import PainPoints from '../../components/funnel/PainPoints';
import SolutionSection from '../../components/funnel/SolutionSection';
import SocialProof from '../../components/funnel/SocialProof';
import QualificationForm from '../../components/funnel/QualificationForm';
import PostFormCTA from '../../components/funnel/PostFormCTA';
import { trackEvent, getUtmParams, generateSessionId, type SemaforoColor } from '../../services/funnelTracker';
import { SITE_CONFIG } from '../../config/constants';

interface FormResult {
  semaforo: {
    dolor: SemaforoColor;
    capacidad: SemaforoColor;
    timing: SemaforoColor;
    inversion: SemaforoColor;
    score: number;
  };
  leadId?: number;
}

export default function LandingV2() {
  const videoRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [formResult, setFormResult] = useState<FormResult | null>(null);

  // Initialize session and track page view on mount
  useEffect(() => {
    generateSessionId();
    getUtmParams();
    trackEvent('landing_v2_view');
  }, []);

  const scrollToVideo = () => {
    videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFormComplete = (result: FormResult) => {
    setFormResult(result);
    // Scroll to result after a brief delay for animation
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 200);
  };

  return (
    <>
      <Helmet>
        <title>Iniciá tu carrera en Data Engineering - Luciano Argolo</title>
        <meta
          name="description"
          content="Bootcamp de Data Engineering. 10 semanas, proyecto real, SQL avanzado y Databricks. Descubrí si es para vos."
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white overflow-x-hidden">
        {/* ============================================ */}
        {/* SECTION 1: HERO */}
        {/* ============================================ */}
        <div className="relative">
          <MatrixBackground color="#00ff41" />
          <div className="relative z-10 bg-black/70">
            <HeroV2 onScrollToVideo={scrollToVideo} />
          </div>
        </div>

        {/* ============================================ */}
        {/* DOWNLOAD PROGRAM CTA */}
        {/* ============================================ */}
        <div className="relative py-10 border-b border-green-500/20">
          <MatrixBackground color="#00ff41" animated={false} />
          <div className="relative z-10 bg-black/80">
            <div className="max-w-xl mx-auto text-center px-6 py-6">
              <a
                href={SITE_CONFIG.urls.programaPDF}
                download="Programa-Completo-Bootcamp-DE.pdf"
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Descargar programa completo
              </a>
              <p className="text-sm text-gray-500 mt-3">PDF con las 10 semanas, cursos extra y +200 ejercicios</p>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* SECTION 2: VSL VIDEO */}
        {/* ============================================ */}
        <div ref={videoRef} className="relative">
          <MatrixBackground color="#00ff41" animated={false} />
          <div className="relative z-10 bg-black/80">
            <VSLSection videoSrc="/videos/Tecnico_radiologo.mp4" />
          </div>
        </div>

        {/* ============================================ */}
        {/* SECTION 3: PAIN POINTS */}
        {/* ============================================ */}
        <div className="relative">
          <MatrixBackground color="#00ff41" animated={false} />
          <div className="relative z-10 bg-black/85">
            <PainPoints />
          </div>
        </div>

        {/* ============================================ */}
        {/* SECTION 4: SOLUTION */}
        {/* ============================================ */}
        <div className="relative">
          <MatrixBackground color="#00ff41" animated={false} />
          <div className="relative z-10 bg-black/80">
            <SolutionSection onScrollToForm={scrollToForm} />
          </div>
        </div>

        {/* ============================================ */}
        {/* SECTION 5: SOCIAL PROOF */}
        {/* ============================================ */}
        <div className="relative">
          <MatrixBackground color="#00ff41" animated={false} />
          <div className="relative z-10 bg-black/85">
            <SocialProof />
          </div>
        </div>

        {/* ============================================ */}
        {/* SECTION 6: QUALIFICATION FORM */}
        {/* ============================================ */}
        <div ref={formRef} className="relative">
          <MatrixBackground color="#3b82f6" animated={false} />
          <div className="relative z-10 bg-black/80">
            {!formResult ? (
              <QualificationForm onComplete={handleFormComplete} />
            ) : (
              /* SECTION 7: POST-FORM RESULT */
              <PostFormCTA semaforo={formResult.semaforo} />
            )}
          </div>
        </div>

        {/* ============================================ */}
        {/* FOOTER */}
        {/* ============================================ */}
        <footer className="relative bg-black border-t border-gray-800 py-8 px-4">
          <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Luciano Argolo - Data Engineering Hub</p>
            <p className="mt-2">
              <a href="/" className="hover:text-green-400 transition-colors">
                Volver al sitio principal
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
